// CopyButton — one-click copy for refs, part numbers, IDs, etc.
//
// Shows a Copy icon, swaps to a green Check for ~1.5s after a successful copy
// to confirm. Falls back to a window.prompt if Clipboard API isn't available
// (older browsers, non-HTTPS dev contexts).
//
// Stops event propagation so it works safely inside Link / clickable rows.

import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

interface Props {
  value: string;
  /** ARIA label + native tooltip text. Defaults to "Copy". */
  label?: string;
  /** Visual size — affects icon + padding. */
  size?: 'sm' | 'xs';
  /** ms to show the green "copied" state. Default 1500. */
  resetMs?: number;
}

export function CopyButton({ value, label = 'Copy', size = 'sm', resetMs = 1500 }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), resetMs);
    } catch {
      window.prompt(`Copy ${label.toLowerCase()}:`, value);
    }
  };

  const iconSize = size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  // Phones: a taller hit area (about 30 x 34px). The negative margin is vertical only, so the row
  // does not grow; a horizontal one made the button overhang its container. From sm up, compact.
  const padding = size === 'xs' ? 'px-2 py-2 -my-1 sm:my-0 sm:px-1 sm:py-0.5' : 'px-2 py-2.5 -my-1.5 sm:my-0 sm:px-1.5 sm:py-1';

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : label}
      aria-label={label}
      className={`inline-flex items-center justify-center rounded transition-colors ${padding} ${
        copied
          ? 'text-success'
          : 'text-foreground-muted hover:text-foreground hover:bg-muted'
      }`}
    >
      {copied ? <Check className={iconSize} strokeWidth={2.5} /> : <Copy className={iconSize} />}
    </button>
  );
}
